import React, { useMemo, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import {
  CloseRounded,
  KeyboardArrowRightRounded,
  KeyboardArrowDownRounded,
  Circle,
} from "@mui/icons-material";
import {
  ROUTE_CAJA,
  ROUTE_CLIENTES,
  ROUTE_COCINAS,
  ROUTE_COMPANY_CONFIG,
  ROUTE_COMPRAS,
  ROUTE_CUADRATURAS,
  ROUTE_EDITOR,
  ROUTE_MANTENEDOR_CAJAS,
  ROUTE_PEDIDOS,
  ROUTE_PEDIDOS_PRODUCTOS,
  ROUTE_PRODUCTOS_INSUMO,
  ROUTE_PRODUCTOS_LIST,
  ROUTE_PROVEEDORES,
  ROUTE_REPORTE,
  ROUTE_REPORTE_VENTA,
  ROUTE_SALAS,
  ROUTE_SUCURSALES,
  ROUTE_TARJETA_EXISTENCIA_DIARIA,
  ROUTE_USUARIOS,
  ROUTE_VENTAS,
} from "@/toolbox/constants/route-map";

import {
  Inventory2Outlined,
  ReceiptLong,
  PointOfSale,
  ShoppingCart,
  SettingsOutlined,
  PeopleAltOutlined,
  ManageAccountsOutlined,
  ApartmentOutlined,
  RestaurantOutlined,
  Equalizer,
  WorkspacesOutlined,
  Store,
} from "@mui/icons-material";
import { KEY_EMPRESA, KEY_USER_DATA } from "@/toolbox/constants/local-storage";
import { useLocalStorage } from "@/toolbox/hooks/local-storage.hook";
import { readLocalStorage } from "@/toolbox/helpers/local-storage-helper";
import { ROLE_VENDEDOR } from "@/toolbox/defaults/static-roles";
import "./drawer.scss";

export const DrawerLateral = ({ showDrawer, setShowDrawer }) => {
  const [dataEmpresa] = useLocalStorage(KEY_EMPRESA, { mesas: 0, id_tipo_empresa: 0 });
  const dataUsuario = readLocalStorage(KEY_USER_DATA);
  const role = dataUsuario?.user?.rol;
  const { pathname } = useLocation();
  const [openGroups, setOpenGroups] = useState({});

  const toggleGroup = (key) => setOpenGroups((p) => ({ ...p, [key]: !p[key] }));

  const menu = useMemo(() => {
    const notCajeroNiMesero = role && role !== "Cajero" && role !== "Mesero";
    const notVendedor = role !== ROLE_VENDEDOR;
    return [
      {
        key: "productos",
        label: "Productos",
        icon: <Store />,
        children: [
          { label: "Gestionar productos", to: ROUTE_EDITOR },
          { label: "Mis productos", to: ROUTE_PRODUCTOS_LIST },
        ],
        guard: !!role && role !== "Mesero",
      },
      { key: "insumos", label: "Insumos", icon: <Inventory2Outlined />, to: ROUTE_PRODUCTOS_INSUMO, guard: !!notCajeroNiMesero },
      {
        key: "pedidos",
        label: "Pedidos",
        icon: <ReceiptLong />,
        children: [
          { label: "Gestionar pedidos", to: ROUTE_PEDIDOS },
          { label: "Vista cocina", to: ROUTE_PEDIDOS_PRODUCTOS },
        ],
      },
      { key: "compras", label: "Compras", icon: <ShoppingCart />, to: ROUTE_COMPRAS, guard: !!notCajeroNiMesero },
      {
        key: "caja",
        label: "Caja",
        icon: <PointOfSale />,
        children: [
          { label: "Gestionar caja", to: ROUTE_CAJA },
          { label: "Cajas", to: ROUTE_MANTENEDOR_CAJAS },
          { label: "Ver cuadratura", to: ROUTE_CUADRATURAS },
        ],
        guard: notVendedor,
      },
      { key: "ventas", label: "Ventas", icon: <WorkspacesOutlined />, to: ROUTE_VENTAS, guard: notVendedor },
      { key: "tarjeta", label: "Tarjeta de existencia", icon: <WorkspacesOutlined />, to: ROUTE_TARJETA_EXISTENCIA_DIARIA, guard: notVendedor },
      {
        key: "empresa",
        label: "Gestionar Empresa",
        icon: <ApartmentOutlined />,
        children: [{ label: "Sucursales", to: ROUTE_SUCURSALES }],
        guard: !!notCajeroNiMesero,
      },
      { key: "usuarios", label: "Gestionar usuarios", icon: <ManageAccountsOutlined />, to: ROUTE_USUARIOS, guard: !!notCajeroNiMesero },
      { key: "clientes", label: "Gestionar clientes", icon: <PeopleAltOutlined />, to: ROUTE_CLIENTES, guard: !!notCajeroNiMesero },
      { key: "proveedores", label: "Gestionar proveedores", icon: <RestaurantOutlined />, to: ROUTE_PROVEEDORES, guard: !!notCajeroNiMesero },
      {
        key: "salas",
        label: "Gestionar salas",
        icon: <RestaurantOutlined />,
        children: [
          { label: "Salas / Mesas", to: ROUTE_SALAS },
          ...(dataEmpresa.id_tipo_empresa === 2 || dataEmpresa.id_tipo_empresa === 3
            ? [{ label: "Cocinas", to: ROUTE_COCINAS }]
            : []),
        ],
        guard: dataEmpresa?.mesas === 1 && notVendedor,
      },
      {
        key: "reportes",
        label: "Reportes",
        icon: <Equalizer />,
        children: [
          { label: "Ver reportes", to: ROUTE_REPORTE },
          { label: "Reporte de ventas", to: ROUTE_REPORTE_VENTA },
        ],
        guard: !!notCajeroNiMesero,
      },
      { key: "config", label: "Configuraciones", icon: <SettingsOutlined />, to: ROUTE_COMPANY_CONFIG, guard: role === "Administrador" },
    ].filter((m) => m.guard === undefined || m.guard);
  }, [role, dataEmpresa]);

  return (
    <Drawer
      anchor="left"
      open={showDrawer}
      onClose={() => setShowDrawer(false)}
      transitionDuration={350}
      PaperProps={{
        sx: {
          width: 270,
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(10px)",
          borderRight: "1px solid rgba(0,0,0,0.05)",
          boxShadow: "4px 0 24px rgba(0,0,0,0.08)",
          borderRadius: "0 18px 18px 0",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box className="lux-header">
        <Typography className="lux-title">NUPY</Typography>
        <IconButton onClick={() => setShowDrawer(false)} className="lux-close">
          <CloseRounded fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      <List className="lux-list">
        {menu.map((node) => {
          const hasChildren = !!node.children;
          
            const childActive = node.children?.some((c) =>
                pathname === c.to || pathname.startsWith(`${c.to}/`)
            );
            const isActive = node.to
                ? pathname === node.to || pathname.startsWith(`${node.to}/`)
                : childActive;

          const isOpen = openGroups[node.key];
          return (
            <Box key={node.key}>
              <ListItemButton
                onClick={() => (hasChildren ? toggleGroup(node.key) : null)}
                component={hasChildren ? "div" : Link}
                to={hasChildren ? undefined : node.to}
                className={`lux-item ${isActive ? "active" : ""}`}
              >
                <ListItemIcon className="lux-icon">{node.icon}</ListItemIcon>
                <ListItemText primary={node.label} primaryTypographyProps={{ className: "lux-label" }} />
                {hasChildren && (
                  <Box className="lux-arrow">
                    {isOpen ? <KeyboardArrowDownRounded /> : <KeyboardArrowRightRounded />}
                  </Box>
                )}
              </ListItemButton>

              {hasChildren && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  {node.children.map((child) => (
                    <ListItemButton
                      key={child.label}
                      component={Link}
                      to={child.to}
                      className={`lux-subitem ${pathname === child.to ? "sub-active" : ""}`}
                    >
                      <ListItemIcon className="lux-subicon">
                        <Circle sx={{ fontSize: 6, color: "#1976D2" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{ className: "lux-sublabel" }}
                      />
                    </ListItemButton>
                  ))}
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>

      <Divider />
      <Typography className="lux-footer">© 2025 Softnet</Typography>
    </Drawer>
  );
};
